import React from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const statusConfig = {
  active: { color: 'text-green-500', label: 'Active', icon: 'Zap' },
  scheduled: { color: 'text-blue-500', label: 'Scheduled', icon: 'Clock' },
  ended: { color: 'text-gray-400', label: 'Ended', icon: 'MinusCircle' },
  sold: { color: 'text-purple-500', label: 'Sold', icon: 'CheckCircle' },
  cancelled: { color: 'text-red-400', label: 'Cancelled', icon: 'XCircle' },
};

const BidHistoryTable = ({ bids, loading }) => {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
        <div className="h-12 bg-muted/40 border-b border-border" />
        {[1, 2, 3, 4, 5]?.map(i => (
          <div key={i} className="h-14 border-b border-border/50 bg-muted/10" />
        ))}
      </div>
    );
  }

  if (!bids?.length) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="History" size={40} className="mx-auto text-muted-foreground mb-3" />
        <h3 className="text-base font-semibold text-foreground mb-1">No bid history</h3>
        <p className="text-muted-foreground text-sm">Your bidding history will appear here</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Desktop Table Header */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-muted/30 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        <div className="col-span-4">Auction</div>
        <div className="col-span-2">Auction Status</div>
        <div className="col-span-2 text-right">Bid Amount</div>
        <div className="col-span-2 text-right">Result</div>
        <div className="col-span-2 text-right">Date</div>
      </div>

      {bids?.map((bid, i) => {
        const auction = bid?.auction;
        const statusCfg = statusConfig?.[auction?.status] || statusConfig?.ended;
        const bidDate = bid?.created_at ? new Date(bid?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
        const isWon = bid?.is_winning && (auction?.status === 'sold' || auction?.status === 'ended');
        const isWinning = bid?.is_winning && auction?.status === 'active';
        const isOutbid = !bid?.is_winning && (auction?.status === 'active' || auction?.status === 'scheduled');

        const ResultBadge = () => {
          if (isWon) return <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-500"><Icon name="Trophy" size={11} />Won</span>;
          if (isWinning) return <span className="inline-flex items-center gap-1 text-xs font-medium text-green-500"><Icon name="Crown" size={11} />Winning</span>;
          if (isOutbid) return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-400"><Icon name="AlertTriangle" size={11} />Outbid</span>;
          return <span className="text-xs text-muted-foreground">—</span>;
        };

        return (
          <div
            key={bid?.id || i}
            className="border-b border-border/50 hover:bg-muted/20 transition-colors"
          >
            {/* Desktop Row */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3.5 items-center">
              {/* Auction */}
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  {auction?.featured_image_url ? (
                    <AppImage
                      src={auction?.featured_image_url}
                      alt={auction?.title || 'Auction item'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Package" size={14} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                <p className="text-sm font-medium text-foreground truncate">{auction?.title || 'Untitled Auction'}</p>
              </div>
              {/* Auction Status */}
              <div className="col-span-2">
                <span className={`text-xs font-medium flex items-center gap-1 ${statusCfg?.color}`}>
                  <Icon name={statusCfg?.icon} size={11} />
                  {statusCfg?.label}
                </span>
              </div>
              {/* Bid Amount */}
              <div className="col-span-2 text-right">
                <span className="text-sm font-semibold text-foreground">
                  ${parseFloat(bid?.bid_amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {/* Result */}
              <div className="col-span-2 text-right">
                <ResultBadge />
              </div>
              {/* Date */}
              <div className="col-span-2 text-right">
                <span className="text-xs text-muted-foreground">{bidDate}</span>
              </div>
            </div>

            {/* Mobile Card */}
            <div className="md:hidden px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  {auction?.featured_image_url ? (
                    <AppImage
                      src={auction?.featured_image_url}
                      alt={auction?.title || 'Auction item'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Package" size={14} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground truncate">{auction?.title || 'Untitled Auction'}</p>
                    <ResultBadge />
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`text-xs font-medium flex items-center gap-1 ${statusCfg?.color}`}>
                      <Icon name={statusCfg?.icon} size={10} />
                      {statusCfg?.label}
                    </span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs font-semibold text-foreground">
                      ${parseFloat(bid?.bid_amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground">{bidDate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BidHistoryTable;
